import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';
import {
  TrainingPlanBasicsFragment,
  TrainingPlanTemplatesDocument,
  TrainingPlanTemplatesFilter,
} from '../../../graphql/types';
import {
  EventCategories,
  EventLabels,
  EventNames,
} from '../../../utils/trackingEvents';
import LoadingPanel from '../../LoadingPanel';
import GenericError from '../../reusable/GenericError';
import { FeatureHeader } from '../../reusable/Header/FeatureHeader';
import { Link } from '../../reusable/Link';
import LoadMore from '../../reusable/LoadMore';
import PaginationWaypoint from '../../reusable/PaginationWaypoint';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '../../reusable/Table';
import Text, { TextColor, TextKind } from '../../reusable/Text';
import { TrainingPlanTemplateFilters } from './TrainingPlanTemplateFilters';
import { useTrainingPlanAttributeLabels } from './useTrainingPlanAttributeLabels';
import { AttributeLabels } from './util';

export const StyledPage = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow-y: hidden;
`;

export const StyledMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding:  24px;
  padding-bottom: 64px;
  max-height: 100%;
  overflow-y: hidden;
`;

const StyledEmptyTable = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 128px;
`;

interface TemplateItemProps {
  template: TrainingPlanBasicsFragment;
  labels: AttributeLabels;
}

const TemplateItem: React.FC<TemplateItemProps> = ({ template, labels }) => {
  const { memberId } = useParams();

  return (
    <TableRow>
      <TableCell $paddingOverride="10px">
        <Link
          to={`/app/members/${memberId}/trainingPlan/${template.id}?navigationDepth=2`}
          trackingData={{
            eventName: EventNames.LinkClick,
            event_label: EventLabels.TrainingPlanTemplateSelect,
            training_plan_template_id: template.id,
            event_category: EventCategories.TrainingPlans,
            member_id: memberId,
          }}
        >
          <Text kind={TextKind.ButtonM}>{template.name}</Text>
        </Link>
      </TableCell>

      <TableCell $paddingOverride="10px" align="center">
        <Text kind={TextKind.BodyM}>{template.type.name}</Text>
      </TableCell>
      <TableCell $paddingOverride="10px" align="center">
        <Text kind={TextKind.BodyM}>
          {labels.equipmentCategories.get(template.equipmentCategory) ?? ''}
        </Text>
      </TableCell>
      <TableCell $paddingOverride="10px" align="center">
        <Text kind={TextKind.BodyM}>
          {labels.difficulties[template.difficulty] ?? template.difficulty}
        </Text>
      </TableCell>
      <TableCell $paddingOverride="10px" align="center">
        <Text kind={TextKind.BodyM}>
          {labels.durations[template.durationMinutes] ??
            template.durationMinutes}
        </Text>
      </TableCell>
      <TableCell $paddingOverride="10px" align="center">
        <Text kind={TextKind.BodyM}>
          {labels.frequencies[template.frequency] ?? template.frequency}
        </Text>
      </TableCell>
      <TableCell $paddingOverride="10px" align="center">
        <Text kind={TextKind.BodyM}>
          {labels.phases[template.phase] ?? template.phase}
        </Text>
      </TableCell>
    </TableRow>
  );
};

const PAGE_SIZE = 100;

interface TrainingPlanTemplatesProps {}

export const TrainingPlanTemplates: React.FC<
  TrainingPlanTemplatesProps
> = () => {
  const [filter, setFilter] = useState<TrainingPlanTemplatesFilter>({});

  const labels = useTrainingPlanAttributeLabels();

  const {
    data: trainingPlanTemplatesData,
    fetchMore: fetchMoreTrainingPlanTemplates,
    networkStatus: trainingPlanTemplatesNetworkStatus,
    error,
  } = useQuery(TrainingPlanTemplatesDocument, {
    variables: {
      input: {
        first: PAGE_SIZE,
      },
      filter,
    },
    notifyOnNetworkStatusChange: true,
  });
  const isFetchingMoreTemplates =
    trainingPlanTemplatesNetworkStatus === NetworkStatus.fetchMore;
  const isLoadingTemplates =
    trainingPlanTemplatesNetworkStatus === NetworkStatus.loading;
  const isRefetchingTemplates =
    trainingPlanTemplatesNetworkStatus === NetworkStatus.refetch ||
    trainingPlanTemplatesNetworkStatus === NetworkStatus.setVariables;
  const templatesPageInfo =
    trainingPlanTemplatesData?.trainingPlanTemplates.pageInfo;
  const hasNextPage = templatesPageInfo?.hasNextPage;
  const endCursor = templatesPageInfo?.endCursor;

  const fetchMoreTemplates = async () => {
    if (!hasNextPage || !endCursor || isFetchingMoreTemplates) {
      return;
    }
    return await fetchMoreTrainingPlanTemplates({
      variables: {
        input: {
          first: PAGE_SIZE,
          after: endCursor,
        },
        filter,
      },
    });
  };

  const templates =
    trainingPlanTemplatesData?.trainingPlanTemplates.edges || [];

  return (
    <StyledPage>
      <FeatureHeader
        state="create"
        name="training plan"
        trackingData={{
          event_category: EventCategories.TrainingPlans,
        }}
      />
      <StyledMain>
        <Text kind={TextKind.TitleL}>Select a training plan template</Text>

        <TrainingPlanTemplateFilters
          setFilter={setFilter}
          trackingData={{ event_category: EventCategories.TrainingPlans }}
        />

        <TableContainer
          style={{
            overflow: 'auto',
            position: 'relative',
          }}
        >
          {isLoadingTemplates && <LoadingPanel noBorder height={300} />}
          {isRefetchingTemplates && <LoadingPanel overlay noBorder />}
          {error && (
            <GenericError testId="trainingPlanTemplates-error" error={error} />
          )}
          {!error &&
            !isLoadingTemplates &&
            !isRefetchingTemplates &&
            templates?.length === 0 && (
              <StyledEmptyTable>
                <Text kind={TextKind.TitleXs}>No templates found.</Text>
              </StyledEmptyTable>
            )}
          {!isLoadingTemplates && templates?.length > 0 && (
            <Table>
              <colgroup>
                <col width="auto" />
                <col width="10%" />
                <col width="12%" />
                <col width="10%" />
                <col width="10%" />
                <col width="10%" />
                <col width="10%" />
              </colgroup>
              <TableHead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                <TableRow>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Training Plan
                    </Text>
                  </TableCell>
                  <TableCell align="center">
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Type
                    </Text>
                  </TableCell>
                  <TableCell align="center">
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Equipment
                    </Text>
                  </TableCell>
                  <TableCell align="center">
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Level
                    </Text>
                  </TableCell>
                  <TableCell align="center">
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Duration
                    </Text>
                  </TableCell>
                  <TableCell align="center">
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Frequency
                    </Text>
                  </TableCell>
                  <TableCell align="center">
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Phase
                    </Text>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trainingPlanTemplatesData?.trainingPlanTemplates.edges.map(
                  ({ node: template }) => (
                    <TemplateItem
                      key={template.id}
                      template={template}
                      labels={labels}
                    />
                  ),
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {hasNextPage && <PaginationWaypoint callback={fetchMoreTemplates} />}

        {hasNextPage && (
          <LoadMore
            loadMore={fetchMoreTemplates}
            isFetchingMore={isFetchingMoreTemplates}
          />
        )}
      </StyledMain>
    </StyledPage>
  );
};
